import React, { useEffect, useMemo, useState } from 'react';
import {
  CURRICULUM_PLANNER_PRIVACY_BODY,
  CURRICULUM_PLANNER_PRIVACY_TITLE,
  CURRICULUM_PLANNER_TERMS_BODY,
  CURRICULUM_PLANNER_TERMS_TITLE,
} from '../../constants/signupLegalKorean';
import { resendSignupConfirmationEmail, signUpWithEmailProfile } from '../../services/authService';
import { fetchSignupLegalDocuments } from '../../services/signupLegalService';
import { recordLegalConsent } from '../../services/legalConsentService';
import SignUpLegalDetailModal from './SignUpLegalDetailModal';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

interface SignUpModalProps {
  open: boolean;
  onClose: () => void;
  /** 이메일 인증 없이 즉시 세션이 생긴 경우(프로젝트 설정에 따라) */
  onSignedIn?: () => void | Promise<void>;
}

type LegalDetailKind = 'terms' | 'privacy';

const SignUpModal: React.FC<SignUpModalProps> = ({ open, onClose, onSignedIn }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [fullName, setFullName] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doneInfo, setDoneInfo] = useState<string | null>(null);
  const [serverLegal, setServerLegal] = useState({
    terms_html: '',
    privacy_html: '',
    terms_updated_at: '',
    privacy_updated_at: '',
  });
  const [legalDetail, setLegalDetail] = useState<LegalDetailKind | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void fetchSignupLegalDocuments().then((leg) => {
      if (!cancelled) setServerLegal(leg);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (open) return;
    setEmail('');
    setPassword('');
    setPassword2('');
    setFullName('');
    setAgreeTerms(false);
    setAgreePrivacy(false);
    setError(null);
    setDoneInfo(null);
    setBusy(false);
    setServerLegal({ terms_html: '', privacy_html: '', terms_updated_at: '', privacy_updated_at: '' });
    setLegalDetail(null);
  }, [open]);

  const termsDisplayHtml = useMemo(() => {
    const t = serverLegal.terms_html.trim();
    if (t) return t;
    return `<pre class="cp-auth-legal-pre">${escapeHtml(CURRICULUM_PLANNER_TERMS_BODY)}</pre>`;
  }, [serverLegal.terms_html]);

  const privacyDisplayHtml = useMemo(() => {
    const t = serverLegal.privacy_html.trim();
    if (t) return t;
    return `<pre class="cp-auth-legal-pre">${escapeHtml(CURRICULUM_PLANNER_PRIVACY_BODY)}</pre>`;
  }, [serverLegal.privacy_html]);

  if (!open) return null;

  const canSubmit =
    email.trim().includes('@') &&
    password.length >= 6 &&
    password === password2 &&
    fullName.trim().length >= 1 &&
    agreeTerms &&
    agreePrivacy;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!canSubmit) {
      setError('필수 항목과 약관 동의를 확인해 주세요. 비밀번호는 6자 이상이어야 합니다.');
      return;
    }
    setBusy(true);
    try {
      const data = await signUpWithEmailProfile({
        email: email.trim(),
        password,
        fullName: fullName.trim(),
      });
      // 이메일 인증이 즉시 세션을 주는 경우에만 로그인 상태이므로 이때만 기록할 수 있다.
      // 인증 메일 확인 후 최초 로그인 시에는 App 레벨 동의 게이트가 대신 기록한다.
      if (data.session) {
        try {
          await recordLegalConsent({
            method: 'email_signup',
            termsVersion: serverLegal.terms_updated_at,
            privacyVersion: serverLegal.privacy_updated_at,
          });
        } catch (consentErr) {
          console.warn('recordLegalConsent(email_signup):', consentErr);
        }
        await onSignedIn?.();
        onClose();
      } else {
        setDoneInfo('인증 메일을 보냈습니다. 메일함의 링크를 확인한 뒤 로그인해 주세요.');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '회원가입에 실패했습니다.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    if (!email.trim().includes('@')) {
      setError('이메일을 입력해 주세요.');
      return;
    }
    setBusy(true);
    try {
      await resendSignupConfirmationEmail(email.trim());
      setDoneInfo('인증 메일을 다시 보냈습니다.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '재전송에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  if (doneInfo) {
    return (
      <div
        className="cp-modal-backdrop cp-auth-modal-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-signup-done-title"
        onClick={onClose}
      >
        <div className="cp-modal cp-auth-modal cp-auth-modal--wide" onClick={(e) => e.stopPropagation()}>
          <h2 id="cp-signup-done-title" className="cp-auth-modal-title">
            회원가입
          </h2>
          <p className="cp-auth-modal-hint">{doneInfo}</p>
          {error && <p className="cp-auth-modal-error">{error}</p>}
          <div className="cp-modal-footer cp-leave-modal-actions">
            <button type="button" className="cp-btn-primary" onClick={onClose}>
              확인
            </button>
          </div>
          <div className="cp-auth-modal-resend-row">
            <button type="button" className="cp-auth-modal-link-btn" onClick={() => void handleResend()} disabled={busy}>
              인증 메일 다시 보내기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="cp-modal-backdrop cp-auth-modal-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-signup-title"
        onClick={onClose}
      >
        <div className="cp-modal cp-auth-modal cp-auth-modal--wide" onClick={(e) => e.stopPropagation()}>
          <h2 id="cp-signup-title" className="cp-auth-modal-title">
            회원가입
          </h2>
          <form onSubmit={handleSubmit} className="cp-auth-modal-form cp-auth-modal-form--scroll">
            <label className="cp-auth-modal-label">이메일 *</label>
            <input
              type="email"
              className="cp-auth-modal-input"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <label className="cp-auth-modal-label">비밀번호 * (6자 이상)</label>
            <input
              type="password"
              className="cp-auth-modal-input"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <label className="cp-auth-modal-label">비밀번호 확인 *</label>
            <input
              type="password"
              className="cp-auth-modal-input"
              autoComplete="new-password"
              placeholder="비밀번호를 다시 입력하세요"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
            />

            <label className="cp-auth-modal-label">이름 *</label>
            <input
              type="text"
              className="cp-auth-modal-input"
              autoComplete="name"
              placeholder="홍길동"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />

            <section className="cp-auth-legal-block cp-auth-legal-block--compact">
              <h3 className="cp-auth-legal-heading">약관 동의 (필수)</h3>
              <p className="cp-auth-legal-intro">
                아래 항목을 눌러 전문을 확인한 뒤, 모달에서 동의할 수 있습니다. 표시되는 내용은 서비스{' '}
                <strong>이용약관</strong>·<strong>개인정보처리방침</strong>의 <strong>현행</strong> 버전과
                동일합니다.
              </p>
              <p className="cp-auth-legal-fullpage-links">
                전문 페이지(새 탭):{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer">
                  이용약관
                </a>
                {' · '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">
                  개인정보처리방침
                </a>
                {' · '}
                <a href="/terms#refund" target="_blank" rel="noopener noreferrer">
                  환불 기준
                </a>
              </p>
              <button
                type="button"
                className={'cp-auth-legal-consent-row' + (agreeTerms ? ' is-agreed' : '')}
                onClick={() => setLegalDetail('terms')}
              >
                <input type="checkbox" readOnly tabIndex={-1} checked={agreeTerms} className="cp-auth-legal-checkbox-accent" />
                <span className="cp-auth-legal-consent-text">
                  <span className="cp-auth-legal-consent-title">{CURRICULUM_PLANNER_TERMS_TITLE}</span>
                  <span className="cp-auth-legal-consent-hint">탭하여 전문 보기 및 동의</span>
                </span>
              </button>
              <button
                type="button"
                className={'cp-auth-legal-consent-row' + (agreePrivacy ? ' is-agreed' : '')}
                onClick={() => setLegalDetail('privacy')}
              >
                <input type="checkbox" readOnly tabIndex={-1} checked={agreePrivacy} className="cp-auth-legal-checkbox-accent" />
                <span className="cp-auth-legal-consent-text">
                  <span className="cp-auth-legal-consent-title">{CURRICULUM_PLANNER_PRIVACY_TITLE}</span>
                  <span className="cp-auth-legal-consent-hint">탭하여 전문 보기 및 동의</span>
                </span>
              </button>
            </section>

            {error && <p className="cp-auth-modal-error">{error}</p>}

            <div className="cp-modal-footer cp-leave-modal-actions">
              <button type="button" className="cp-btn-secondary" onClick={onClose}>
                취소
              </button>
              <button type="submit" className="cp-btn-primary" disabled={!canSubmit || busy}>
                {busy ? '처리 중…' : '회원가입 완료'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {legalDetail === 'terms' ? (
        <SignUpLegalDetailModal
          title={CURRICULUM_PLANNER_TERMS_TITLE}
          bodyHtml={termsDisplayHtml}
          emptyFallback="표시할 약관 본문이 없습니다."
          onCancel={() => setLegalDetail(null)}
          onConfirm={() => {
            setAgreeTerms(true);
            setLegalDetail(null);
          }}
        />
      ) : null}
      {legalDetail === 'privacy' ? (
        <SignUpLegalDetailModal
          title={CURRICULUM_PLANNER_PRIVACY_TITLE}
          bodyHtml={privacyDisplayHtml}
          emptyFallback="표시할 개인정보처리방침 본문이 없습니다."
          onCancel={() => setLegalDetail(null)}
          onConfirm={() => {
            setAgreePrivacy(true);
            setLegalDetail(null);
          }}
        />
      ) : null}
    </>
  );
};

export default SignUpModal;
